import { TextField, Checkbox, FormControlLabel, InputAdornment, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { createEmail, getEmail, updateEmail, type Email } from "@/api/email.service";
import { ConfigDropdownForm } from "@/pages/config/ConfigDropdownForm";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNotification } from "@/context/NotificationContext";
import { useLogger } from "@/hooks/useLogger";
import { LogEventType } from "@/api/log.service";

export const EmailConfigPage = () => {
  const [emailConfig, setEmailConfig] = useState<Email>({
    from_email: "",
    from_name: "",
    id: "",
    provider: "",
    smtp_host: "",
    smtp_password: "",
    smtp_port: 0,
    smtp_username: "",
    use_tls: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const { notify } = useNotification();
  const { log } = useLogger();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const handleSaveEmail = async () => {
    if (emailConfig.id) {
      try {
        updateEmail(emailConfig.id, emailConfig);
        notify("Email atualizado com sucesso", "success");
        log(LogEventType.Updated, emailConfig, "email", emailConfig.id, "info");
      } catch (e) {
        notify("Falha ao atualizar email. Tente novamente mais tarde", "error");
        console.error("Error updating email: ", e)
      }
    } else {
      try {
        var newEmail = await createEmail(emailConfig);
        notify("Email criado com sucesso", "success");
        log(LogEventType.Created, emailConfig, "email", newEmail.id, "info");
      } catch (e) {
        notify("Falha ao criar email. Tente novamente mais tarde", "error");
        console.error("Error creating email: ", e)
      }
    }
  };

  useEffect(() => {
    const getEmailConfig = async () => {
      try {
        const email = await getEmail();
        setEmailConfig(email);
      } catch (e) {
        notify("Falha ao buscar email. Tente novamente mais tarde", "error");
        console.error("Error fetching email: ", e)
      }
    }

    getEmailConfig();
  }, [])

  return (
    <ConfigDropdownForm title="Configurações de E-mail" onSave={handleSaveEmail}>
      <TextField
        label="Host SMTP"
        type="string"
        value={emailConfig.smtp_host ?? ""}
        onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })}
        fullWidth
      />

      <TextField
        label="Porta SMTP"
        type="number"
        value={emailConfig.smtp_port ?? 25}
        onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: Number(e.target.value) })}
        fullWidth
      />

      <TextField
        label="Usuário SMTP"
        type="string"
        value={emailConfig.smtp_username ?? ""}
        onChange={(e) => setEmailConfig({ ...emailConfig, smtp_username: e.target.value })}
        fullWidth
      />

      <TextField
        label="Senha SMTP"
        type={showPassword ? 'text' : 'password'}
        value={emailConfig.smtp_password ?? ""}
        onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }
        }}
        fullWidth
      />

      <TextField
        label="Email de Envio"
        type="string"
        value={emailConfig.from_email ?? ""}
        onChange={(e) => setEmailConfig({ ...emailConfig, from_email: e.target.value })}
        fullWidth
      />

      <TextField
        label="Nome de Envio"
        type="string"
        value={emailConfig.from_name ?? ""}
        onChange={(e) => setEmailConfig({ ...emailConfig, from_name: e.target.value })}
        fullWidth
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={!!emailConfig.use_tls}
            onChange={(e) => setEmailConfig({ ...emailConfig, use_tls: e.target.checked })}
          />
        }
        label="Usar TLS"
      />

      <TextField
        label="Provedor"
        type="string"
        value={emailConfig.provider ?? ""}
        onChange={(e) => setEmailConfig({ ...emailConfig, provider: e.target.value })}
        fullWidth
      />
    </ConfigDropdownForm>
  )
}